
import React from 'react';
import Header from '@/components/Header';
import { SidebarProvider } from '@/contexts/SidebarContext';
import AppSidebar from '@/components/AppSidebar';
import DashboardFooter from '@/components/DashboardFooter';
import FeatureErrorBoundary from '@/components/FeatureErrorBoundary';
import DashboardContent from '@/components/dashboard/DashboardContent';
import DashboardInitializer from '@/components/dashboard/initialization/DashboardInitializer';
import { useDashboardState } from '@/components/dashboard/hooks/useDashboardState';
import { useWidgetRefs } from '@/components/dashboard/hooks/useWidgetRefs';
import { useSidebarContext } from '@/contexts/SidebarContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const DashboardLayout: React.FC<{
  isAnalyzing: boolean;
  progress: any;
  crawlResult: any;
  websiteUrl: string;
  activePage: string;
  selectedPageUrl: string | null;
  handleSubmit: (url: string) => void;
  handleCancel: () => void;
  handleNewCrawl: () => void;
  handleNavigate: (page: string) => void;
  handleSelectPage: (url: string) => void;
  widgetRefs: any;
  retryHandlers: any;
}> = ({ 
  isAnalyzing, 
  progress, 
  crawlResult, 
  websiteUrl, 
  activePage, 
  selectedPageUrl, 
  handleSubmit, 
  handleCancel, 
  handleNewCrawl, 
  handleNavigate, 
  handleSelectPage, 
  widgetRefs, 
  retryHandlers 
}) => {
  const { compact } = useSidebarContext();
  const isMobile = useIsMobile();

  return (
    <div className="flex min-h-screen w-full">
      <AppSidebar 
        activePage={activePage} 
        onNavigate={handleNavigate} 
      />
      
      <div className={cn(
        "flex-1 min-w-0 px-6 pb-12 overflow-x-hidden transition-all duration-300",
        !isMobile && (compact ? "ml-16" : "ml-64")
      )}>
        <FeatureErrorBoundary componentName="Dashboard" onRetry={() => window.location.reload()}>
          <DashboardContent 
            isAnalyzing={isAnalyzing}
            progress={progress}
            crawlResult={crawlResult}
            websiteUrl={websiteUrl}
            activePage={activePage}
            selectedPageUrl={selectedPageUrl}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onNewCrawl={handleNewCrawl}
            onNavigate={handleNavigate}
            onSelectPage={handleSelectPage}
            widgetRefs={widgetRefs}
            retryHandlers={retryHandlers}
          />
        </FeatureErrorBoundary>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const {
    isAnalyzing,
    progress,
    crawlResult,
    websiteUrl,
    activePage,
    selectedPageUrl,
    hasLoadedFromState,
    setHasLoadedFromState,
    handleSubmit,
    handleCancel,
    handleNewCrawl,
    handleNavigate,
    handleSelectPage,
    location,
    scrollToSection
  } = useDashboardState();

  const { widgetRefs, retryHandlers } = useWidgetRefs();

  return (
    <div className="min-h-screen">
      <Header />
      
      <DashboardInitializer
        hasLoadedFromState={hasLoadedFromState}
        setHasLoadedFromState={setHasLoadedFromState}
        location={location}
        handleSubmit={handleSubmit}
        scrollToSection={scrollToSection}
      />
      
      <main className="pt-20">
        <SidebarProvider>
          <DashboardLayout 
            isAnalyzing={isAnalyzing}
            progress={progress}
            crawlResult={crawlResult}
            websiteUrl={websiteUrl}
            activePage={activePage}
            selectedPageUrl={selectedPageUrl}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            handleNewCrawl={handleNewCrawl}
            handleNavigate={handleNavigate}
            handleSelectPage={handleSelectPage}
            widgetRefs={widgetRefs}
            retryHandlers={retryHandlers}
          />
        </SidebarProvider>
      </main>
      
      <DashboardFooter />
    </div>
  );
};

export default Dashboard;
